// StreamView.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// CSS
import './StreamView.css';

// Components
import StreamChat from './StreamChat';
import Hls from 'hls.js';

/**
 * StreamView
// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';
 *
 * Viewer-facing component for LIVE_STREAM nodes.
 * Expects secured playback data merged into `node.liveStream` via NodeReadService.
 * 
 * @param {object} node - The LIVE_STREAM node object.
 * 
 * @returns {ReactNode} - StreamView component.
 */
const StreamView = ({ node }) => {

  // ----- STATE ----- //

  /* ----- STATE ----- */

  const [error, setError] = useState(null);

  // ----- REFS ----- //

  /* ----- REFERENCES ----- */

  // Video element reference

  const videoRef = useRef(null);

  // ----- MEMOS ----- //

  /* ----- MEMOIZED VALUES ----- */

  /**
   * Playback URL derived from node.liveStream data.
   */

  const playbackUrl = useMemo(() => {
    if (!node) return null;
    const ls = node.liveStream || {};
    const url = (
      ls.muxPlaybackUrl ||
      ls.playbackUrl ||
      null
    );
    return url;
  }, [node]);

  // ----- EFFECTS ----- //

  /* ----- EFFECTS ----- */

  /**
   * Setup HLS playback if needed, and handle video errors.
   */

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return undefined;

    setError(null);

    let hls = null;
    const isHls = playbackUrl.endsWith('.m3u8');

    if (isHls && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('StreamView: HLS error', { event, data });
        // 412 or similar means stream not ready/offline yet.
        const status = data?.response?.code || data?.xhr?.status;
        if (status === 412) {
          setError('offline');
          return;
        }

        if (data?.fatal) {
          setError('offline');
        }
      });
    } else {
      video.src = playbackUrl;
    }

    /**
     * handleVideoError
     *
     * Handles video error.
     */
    const handleVideoError = () => {
      const mediaError = video.error || null;
      console.error('StreamView: <video> element error', mediaError);
      setError('offline');
    };

    video.addEventListener('error', handleVideoError);

    return () => {
      if (hls) {
        hls.destroy();
      }
      if (video) {
        video.removeEventListener('error', handleVideoError);
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [playbackUrl]);

  /* ----- RENDER ----- */

  if (!playbackUrl) {
    return (
      <div className="stream-view-container">

        {/* STATUS MESSAGE */}
        <div className="stream-view-status">
          Livestream isn&apos;t ready yet. Ask the host to start streaming.
        </div>
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div className="stream-view-container">

      {!error && (
        <div className="stream-view-header">

          {/* LIVE PILL */}
          <span className="stream-view-live-pill">LIVE</span>
        </div>
      )}

      {!error && (

        <div className="stream-view-player-wrapper">

          {/* VIDEO */}
          <video
            className="stream-view-video"
            ref={videoRef}
            controls
            playsInline
            autoPlay
          />

          {/* STATUS MESSAGE */}
          {error && (

            <div className="stream-view-status-bold">
              Livestream is currently offline2.
            </div>
          )}

        </div>
      )}

      {error && (

        <div className="stream-view-cover-wrapper">

          {/* COVER IMAGE */}
          {node?.coverUrl && (
            <img
              className="stream-view-cover-image"
              src={node.coverUrl}
              alt="Stream Cover"
            />
          )}

          {/* STATUS MESSAGE */}
          <div className="stream-view-status-bold">
            Livestream is currently offline.
          </div>

        </div>
      )}

      <StreamChat node={node} />

    </div>
  );
};

// Dev Tools
StreamView.displayName = 'StreamView';

export default StreamView;
